import React from "react";
import PolicyLayout, { Section } from "@/components/PolicyLayout";

const SECTIONS = [
  { id: "intro", label: "1. Overview" },
  { id: "scope", label: "2. Scope & Applicability" },
  { id: "definitions", label: "3. Key Definitions" },
  { id: "info-collected", label: "4. Information We Collect" },
  { id: "purpose", label: "5. Purpose of Collection" },
  { id: "legal-basis", label: "6. Legal Basis" },
  { id: "cookies", label: "7. Cookies & Tracking" },
  { id: "sharing", label: "8. Data Sharing" },
  { id: "payments-data", label: "9. Payment Data (RBI/PCI-DSS)" },
  { id: "retention", label: "10. Retention" },
  { id: "security", label: "11. Security Practices" },
  { id: "rights", label: "12. Your Rights" },
  { id: "children", label: "13. Children's Privacy" },
  { id: "transfers", label: "14. Cross-Border Transfers" },
  { id: "third-party", label: "15. Third-Party Links" },
  { id: "changes", label: "16. Changes" },
  { id: "grievance", label: "17. Grievance Officer" },
  { id: "contact", label: "18. Contact" },
];

export default function Privacy() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      tagline="This Privacy Policy explains how Florvanta Commerce Pvt Ltd collects, uses, discloses, and safeguards your personal data when you use our Platform."
      updated="February 2026"
      sections={SECTIONS}
    >
      <Section id="intro" title="1. Overview">
        <p>
          Florvanta Commerce Pvt Ltd (&quot;<b>Florvanta</b>&quot;, &quot;<b>we</b>&quot;, &quot;<b>us</b>&quot;) values your privacy. This Privacy Policy is published in accordance with:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Section 43A of the Information Technology Act, 2000 read with the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (&quot;<b>SPDI Rules</b>&quot;);</li>
          <li>Rule 3 and Rule 4 of the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021;</li>
          <li>The Digital Personal Data Protection Act, 2023 (&quot;<b>DPDPA</b>&quot;) as and when notified in force;</li>
          <li>Applicable Reserve Bank of India (&quot;<b>RBI</b>&quot;) guidelines, including Card-on-File Tokenisation, Storage of Payment System Data, and Payment Aggregator norms;</li>
          <li>The Consumer Protection (E-Commerce) Rules, 2020.</li>
        </ul>
      </Section>

      <Section id="scope" title="2. Scope &amp; Applicability">
        <p>
          This Policy applies to all users of the Florvanta website, mobile applications, APIs, and any related services (collectively, the &quot;<b>Platform</b>&quot;), including Buyers, Vendors, and visitors.
        </p>
      </Section>

      <Section id="definitions" title="3. Key Definitions">
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Personal Data</b> means any information relating to an identified or identifiable natural person.</li>
          <li><b>Sensitive Personal Data or Information (SPDI)</b> as defined under Rule 3 of the SPDI Rules includes passwords, financial information such as bank account or card details, physical, physiological and mental health condition, biometric information, etc.</li>
          <li><b>Data Fiduciary</b> and <b>Data Principal</b> shall have the meanings ascribed to them in the DPDPA.</li>
        </ul>
      </Section>

      <Section id="info-collected" title="4. Information We Collect">
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Identity data:</b> name, gender, date of birth (optional).</li>
          <li><b>Contact data:</b> email address, mobile number, shipping / billing address.</li>
          <li><b>Account credentials:</b> hashed password (never stored in plain text; hashed using bcrypt).</li>
          <li><b>Vendor KYC data:</b> PAN, GSTIN, business incorporation documents, bank account &amp; IFSC, address proof.</li>
          <li><b>Transaction data:</b> orders, order status, payment method (COD / online), invoices.</li>
          <li><b>Payment data:</b> handled and stored solely by our RBI-authorised Payment Aggregator. Florvanta does <b>not</b> store card numbers, CVV, UPI PIN, or bank passwords on its servers.</li>
          <li><b>Device &amp; usage data:</b> IP address, browser type, device identifiers, session logs, pages visited (for analytics and fraud prevention).</li>
          <li><b>Location data:</b> pincode / city for serviceability checks (grocery, shipping).</li>
        </ul>
      </Section>

      <Section id="purpose" title="5. Purpose of Collection">
        <p>We use your Personal Data for the following purposes:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>To create and manage your account and provide the Platform&apos;s features.</li>
          <li>To process orders, payments, invoices, shipping, returns, and refunds.</li>
          <li>To perform Vendor KYC and comply with Prevention of Money Laundering Act, 2002 (PMLA) and RBI KYC Master Directions where applicable.</li>
          <li>To detect, prevent, and investigate fraud, security incidents, and prohibited activities.</li>
          <li>To respond to customer support and grievance requests.</li>
          <li>To send transactional and, with consent, promotional communications.</li>
          <li>To comply with legal, tax (GST/TCS), and regulatory obligations.</li>
        </ul>
      </Section>

      <Section id="legal-basis" title="6. Legal Basis for Processing">
        <p>Where the DPDPA is in force, we process your Personal Data on the following legal bases:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your <b>consent</b>, freely given, specific, and revocable at any time;</li>
          <li>Performance of a <b>contract</b> to which you are a party;</li>
          <li>Compliance with a <b>legal obligation</b>; and</li>
          <li>For <b>certain legitimate uses</b> permitted under Section 7 of the DPDPA.</li>
        </ul>
      </Section>

      <Section id="cookies" title="7. Cookies &amp; Tracking Technologies">
        <p>
          We use strictly necessary cookies for authentication (HttpOnly, Secure) and analytics cookies to improve the Platform. You can control cookies through your browser settings. Disabling essential cookies may impair functionality such as login and checkout.
        </p>
      </Section>

      <Section id="sharing" title="8. Data Sharing &amp; Disclosure">
        <p>We share Personal Data only with:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Vendors:</b> Buyer&apos;s name, shipping address, and phone number are shared with the fulfilling Vendor solely for order processing.</li>
          <li><b>Payment Aggregators &amp; Banks:</b> for processing payments and refunds in accordance with RBI norms.</li>
          <li><b>Logistics &amp; Courier partners:</b> shipping address, phone number, and order details for delivery.</li>
          <li><b>Regulatory / Government authorities:</b> where required under applicable law, court order, or lawful request.</li>
          <li><b>Technology sub-processors:</b> cloud hosting, email, SMS, WhatsApp providers, bound by strict confidentiality obligations.</li>
        </ul>
        <p>We do <b>not</b> sell your Personal Data to any third party.</p>
      </Section>

      <Section id="payments-data" title="9. Payment Data — RBI &amp; PCI-DSS">
        <p>
          Florvanta uses RBI-authorised Payment Aggregators. In compliance with the <b>RBI Circular on Storage of Payment System Data</b> dated 6 April 2018, all payment data pertaining to Indian users is stored on servers located within India.
        </p>
        <p>
          In compliance with the <b>RBI Guidelines on Restrictions on Storage of Actual Card Data</b> and the <b>Card-on-File (CoF) Tokenisation Framework</b>, we do not store actual card numbers or CVV on our systems. Where you choose to save a card, only a tokenised representation issued by the card network is retained, encrypted, and processed by the Payment Aggregator.
        </p>
        <p>
          Our Payment Aggregator is PCI-DSS Level 1 certified. Payment data in transit is protected by TLS 1.2 or higher.
        </p>
      </Section>

      <Section id="retention" title="10. Data Retention">
        <p>
          We retain Personal Data only for as long as necessary for the purposes set out above or as required under applicable law, including:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Invoices &amp; tax records: 8 years (Section 36 CGST Act, 2017).</li>
          <li>Vendor KYC: for the duration of the relationship plus 5 years thereafter (PMLA).</li>
          <li>Transaction logs: minimum 180 days (Rule 3(1)(g) IT Rules 2021).</li>
          <li>Marketing preferences: until withdrawal of consent.</li>
        </ul>
      </Section>

      <Section id="security" title="11. Security Practices">
        <p>
          We implement &quot;reasonable security practices and procedures&quot; as required under Section 43A of the IT Act and Rule 8 of the SPDI Rules, including:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Passwords stored only as bcrypt salted hashes.</li>
          <li>TLS 1.2+ encryption in transit.</li>
          <li>Role-based access control and audit logging.</li>
          <li>Regular vulnerability assessments and penetration testing.</li>
          <li>ISO/IEC 27001-aligned information security controls (best-effort during MVP; formal certification post-launch).</li>
        </ul>
        <p>
          Despite these measures, no method of transmission over the Internet is 100% secure. In the event of a data breach affecting your Personal Data, we will notify the Indian Computer Emergency Response Team (CERT-In) within six (6) hours of becoming aware, in line with CERT-In Directions dated 28 April 2022, and notify affected users promptly.
        </p>
      </Section>

      <Section id="rights" title="12. Your Rights">
        <p>Subject to applicable law, you have the following rights:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Right to access</b> your Personal Data.</li>
          <li><b>Right to correction and erasure</b> of inaccurate or unnecessary data.</li>
          <li><b>Right to grievance redressal</b> via our Grievance Officer.</li>
          <li><b>Right to nominate</b> another individual to exercise rights in the event of your death or incapacity (DPDPA).</li>
          <li><b>Right to withdraw consent</b> at any time; withdrawal will not affect the lawfulness of processing based on prior consent.</li>
        </ul>
        <p>To exercise your rights, write to <b>privacy@florvanta.co.in</b>.</p>
      </Section>

      <Section id="children" title="13. Children's Privacy">
        <p>
          The Platform is not intended for individuals under the age of eighteen (18). We do not knowingly collect Personal Data from minors. If we become aware of such collection, we will promptly delete the data.
        </p>
      </Section>

      <Section id="transfers" title="14. Cross-Border Transfers">
        <p>
          Payment data is stored within India per RBI guidelines. Certain non-payment data may be processed by sub-processors located outside India; in such cases, transfers occur only to jurisdictions notified by the Central Government under Section 16 of the DPDPA or under contractual safeguards.
        </p>
      </Section>

      <Section id="third-party" title="15. Third-Party Links">
        <p>
          The Platform may contain links to third-party websites. We are not responsible for their privacy practices. Please review their policies separately.
        </p>
      </Section>

      <Section id="changes" title="16. Changes to this Policy">
        <p>
          We may update this Privacy Policy from time to time. Material changes will be notified through email or on-Platform notice. Continued use of the Platform after the effective date constitutes acceptance.
        </p>
      </Section>

      <Section id="grievance" title="17. Grievance Officer">
        <p>
          Under Rule 5(9) of the SPDI Rules and Rule 3(2) of the IT Rules 2021:
        </p>
        <div className="rounded-lg border border-[#E2E8E5] bg-white p-4 text-[#0F3D2E]">
          <div><b>Name:</b> Mr. Debasis Saibo</div>
          <div><b>Company:</b> Florvanta Commerce Private Limited</div>
          <div><b>Email:</b> info@florvanta.co.in</div>
          <div><b>Phone:</b> +91 91474 25375</div>
          <div><b>Address:</b> 63, Sri Arabinda Road, Salkia, Howrah, West Bengal - 711106</div>
          <div><b>Acknowledgement:</b> within 48 hours · <b>Resolution:</b> within 15 days</div>
        </div>
      </Section>

      <Section id="contact" title="18. Contact Us">
        <p>
          For any privacy-related queries: <b>info@florvanta.co.in</b> · General support: <b>info@florvanta.co.in</b> · <b>+91 91474 25375</b>
        </p>
      </Section>
    </PolicyLayout>
  );
}
